import { ReactNode } from "react";

export type Align = "left" | "right" | "center";

export type Accessor<D, ReturnType = any> = keyof D | ((item: D) => ReturnType);

export type SelectionType = "single" | "multi";

export interface ColumnOption {
  id: string;
  header: ReactNode;
}

export interface ColumnOptionWithVisibility extends ColumnOption {
  visible: boolean;
}

export interface ColumnDefinition<D> extends ColumnOption {
  accessor: Accessor<D, ReactNode>;
  align?: Align;
  isSortable?: boolean;
  cell?: (item: D) => ReactNode;
}

export interface ColumnDisplay {
  id: string;
  visible: boolean;
}

export interface DataTableTranslations {
  columnSettings: string;
  update: string;
  cancel: string;
}

export interface SelectionProps<D> {
  selectionType?: SelectionType;
  selectedItems?: Array<string>;
  onSelectedItemsChange?: (selectedItems: Array<string>) => void;
  isItemDisabled?: (item: D) => boolean;
}

export interface SelectionControlProps {
  name: string;
  isChecked: boolean;
  onChange: () => void;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  value?: string;
}

export interface SortProps {
  sortColumn?: string;
  sortDescending?: boolean;
  onSortChange?: (column: string, descending: boolean) => void;
}

// Maintaining this order for the status style list:
export const statusStyles: {
  [key: string]: {
    text: string;
    color: string;
    bg: string;
    index: number;
    description?: string;
  };
} = {
  PENDING: {
    text: "Extracting",
    color: "gray.600",
    bg: "blackAlpha.100",
    index: 5,
    description:
      "This document has been added to the extraction queue and Jack will process it shortly. You will only be charged for the extraction time, not while the document is in the queue. You can safely navigate away and come back to this screen at any time."
  },
  IN_PROGRESS: {
    text: "In Progress",
    color: "blue.600",
    bg: "blue.100",
    index: 7
  },
  ON_HOLD: { text: "On Hold", color: "orange.600", bg: "orange.100", index: 6 },
  FAILED: {
    text: "Failed",
    color: "red.600",
    bg: "red.100",
    index: 4,
    description:
      "This means that Jack has been unable to extract any data or the document uploaded was not expense related. You are able to view which document this was, but no further action will be needed."
  },
  REVIEWED: {
    text: "Reviewing",
    color: "orange.500",
    bg: "#FFDCC2",
    index: 2,
    description:
      "This means that a bookkeeper or accountant has looked at the transaction details that Jack has extracted and is still busy working on, made a change or added something and then saved."
  },
  SYNCED: {
    text: "Synced",
    color: "green.600",
    bg: "green.100",
    index: 11,
    description:
      "This means that the reviewer is happy with all the detail Jack has extracted and taken a position on, may have done some further edits, but has published to the relevant accounting platform."
  },
  COMPLETED: {
    text: "Ready",
    color: "blue.600",
    bg: "blue.100",
    index: 1,
    description:
      "This means that Jack has extracted all the data from the document, taken a position on the Account Code and VAT treatment and is ready to be reviewed by a bookkeeper or accountant before publishing to the accounting platform."
  },
  PUBLISHING: {
    text: "Publishing",
    color: "gray.600",
    bg: "blackAlpha.100",
    index: 2.5,
    description:
      "This means that Jack is currently in the process of publishing to the relevant accounting platform."
  },
  PUBLISHED: {
    text: "Published",
    color: "green.600",
    bg: "green.100",
    index: 3,
    description:
      "This means that the reviewer is happy with all the detail Jack has extracted and taken a position on, may have done some further edits, but has published to the relevant accounting platform."
  },
  EDITED: {
    text: "Edited",
    color: "orange.600",
    bg: "orange.100",
    index: 8
  },
  REPUBLISHED: {
    text: "Published",
    color: "green.600",
    bg: "green.100",
    index: 9
  },
  DELETED: {
    text: "Deleted",
    color: "red.600",
    bg: "red.100",
    index: 10
  },
  DUPLICATE: {
    text: "Duplicate",
    color: "#9A8D00",
    bg: "#FFEA0036",
    index: 12,
    description:
      "This means Jack has identified a duplicate transaction based on the value, supplier, date and document name. You can view both the duplicate and original and either discard or Mark as Ready to work on. "
  },
  DISCARDED: {
    text: "Discarded",
    color: "red.300",
    bg: "red.100",
    index: 13
  },
  RECONCILED: {
    text: "Matched",
    color: "green.600",
    bg: "green.100",
    index: 14
  },
  UNRECONCILED: {
    text: "Unmatched",
    color: "red.600",
    bg: "red.100",
    index: 15
  },
  POSSIBLY_MATCHED: {
    text: "Possibly Matched",
    color: "red.600",
    bg: "red.100",
    index: 16
  },
  "": { text: "All", color: "", bg: "", index: 12 }
};

export type StatusType = keyof typeof statusStyles;

export interface StoredStatuses {
  label: string;
  value: StatusType;
  count: number;
  index: number;
}
